import { useMemo } from 'react';

/**
 * Builds a hierarchical tree from a flat zones array
 * Mutates zones to add 'children' arrays, returns all zones
 */
export const buildZonesTree = (zones = []) => {
    if (!Array.isArray(zones)) return [];

    const _zones = [...zones];

    _zones.forEach(zone => {
        if (!zone.parent) return;

        const parent = _zones.find(z => z.pk === zone.parent);
        if (!parent) return;

        parent.children = parent.children || [];
        if (!parent.children.find(c => c.pk === zone.pk)) {
            parent.children.push(zone);
        }
    });

    return _zones;
};

/**
 * Checks if a value matches the search query supports multi-word search
 */
export const matchSearch = (value, q) => {
    if (!q) return true;
    if (typeof value !== 'string') return false;

    const normalizedValue = value.toLowerCase();
    const searchTerms = q
        .trim()
        .toLowerCase()
        .split(/\s+/);

    return searchTerms.every(term => normalizedValue.includes(term));
};

/**
 * Recursively filters a node and its children based on search query
 */
export const filterNodeHierarchy = (node, q) => {
    const nodeMatches = matchSearch(node.name, q);

    if (!node.children?.length) {
        // Leaf node: return it only if it matches
        return nodeMatches ? node : null;
    }

    const filteredChildren = node.children.map(child => filterNodeHierarchy(child, q)).filter(Boolean);

    // If node itself matches OR has matching children, return it with filtered children
    if (nodeMatches || filteredChildren.length > 0) {
        const result = {
            ...node,
            name: node.name,
            children: filteredChildren
        };
        return result;
    }

    return null;
};

/**
 * Returns zones filtered by search, only root zones
 */
export const getDisplayedZones = (zones, search) => {
    if (!search?.trim()) return zones.filter(z => !z.parent);

    return zones
        .filter(z => !z.parent)
        .map(node => filterNodeHierarchy(node, search.trim()))
        .filter(Boolean);
};

/**
 * Custom hook to manage zones tree with search filtering
 */
export const useZonesTree = (zones, search) => {
    const zonesTree = useMemo(() => buildZonesTree(zones), [zones]);
    const displayedZones = useMemo(() => getDisplayedZones(zonesTree, search), [zonesTree, search]);

    return { zonesTree, displayedZones };
};
